const DATE_FILTER_START = {
  "All Time": null,
  "Last 7 Days": 7,
  "Last 30 Days": 30,
  "Last 90 Days": 90,
};

export const formatTransactionTypeLabel = (rawType) => {
  const type = String(rawType || "").toLowerCase();
  const customLabels = {
    bot_license_fee: "Bot License Fee",
    affiliate_commission: "Affiliate Commission",
    arbitrage_trade_close: "Arbitrage Trade Settlement",
    arbitrage_profit: "Arbitrage Profit",
    crypto_deposit: "Crypto Deposit",
    crypto_withdrawal: "Crypto Withdrawal",
    leadership_reward: "Leadership Reward",
    performance_award: "Performance Award",
    bot_affiliate_profit: "Bot Affiliate Profit",
    trading_affiliate_profit: "Trading Affiliate Profit",
  };
  if (customLabels[type]) return customLabels[type];
  return String(rawType || "Transaction")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

export const isCreditTransaction = (txType, amount) => {
  const normalizedType = String(txType || "").toUpperCase();
  if (
    normalizedType.includes("WITHDRAW") ||
    normalizedType.includes("DEBIT") ||
    normalizedType.includes("FEE")
  ) {
    return false;
  }
  if (
    normalizedType.includes("DEPOSIT") ||
    normalizedType.includes("CREDIT") ||
    normalizedType.includes("BONUS") ||
    normalizedType.includes("REWARD") ||
    normalizedType.includes("COMMISSION")
  ) {
    return true;
  }
  return Number(amount || 0) >= 0;
};

const mapStatus = (normalizedStatus) => {
  if (normalizedStatus === "completed" || normalizedStatus === "confirmed") {
    return { label: "Completed", color: "gold" };
  }
  if (normalizedStatus === "pending" || normalizedStatus === "processing") {
    return { label: "Pending", color: "yellow" };
  }
  return { label: "Failed", color: "red" };
};

export const getDateFilterStart = (dateFilterLabel) => {
  const days = DATE_FILTER_START[dateFilterLabel];
  if (days == null) return null;
  const now = new Date();
  return new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
};

/** Optional ISO/date strings or Date for report date pickers */
export const filterAndNormalizeTransactions = (
  allTransactions,
  {
    typeFilter = "All",
    statusFilter = "All",
    dateFilter = "All Time",
    searchTerm = "",
    rangeStart = null,
    rangeEnd = null,
  },
) => {
  let dateStart = null;
  let rangeEndNorm = null;

  if (rangeStart || rangeEnd) {
    if (rangeStart) {
      dateStart = new Date(rangeStart);
      dateStart.setHours(0, 0, 0, 0);
    }
    if (rangeEnd) {
      rangeEndNorm = new Date(rangeEnd);
      rangeEndNorm.setHours(23, 59, 59, 999);
    }
  } else {
    dateStart = getDateFilterStart(dateFilter);
  }

  const filteredRows = (Array.isArray(allTransactions) ? allTransactions : []).filter((tx) => {
    const rawAmount = Number(tx?.amount || 0);
    if (Math.abs(rawAmount) <= 0) return false;

    const rawType = String(tx?.type || "").toLowerCase();
    const displayType = rawType.replace(/_/g, " ");
    const normalizedStatus = String(tx?.status || "completed").toLowerCase();
    const createdAt = tx?.createdAt ? new Date(tx.createdAt) : null;

    if (dateStart && createdAt && createdAt < dateStart) return false;
    if (rangeEndNorm && createdAt && createdAt > rangeEndNorm) return false;

    if (typeFilter !== "All") {
      const selected = typeFilter.toLowerCase();
      if (!displayType.includes(selected)) return false;
    }

    if (statusFilter !== "All") {
      const selected = statusFilter.toLowerCase();
      const mapped =
        normalizedStatus === "completed" || normalizedStatus === "confirmed"
          ? "completed"
          : normalizedStatus === "pending" || normalizedStatus === "processing"
            ? "pending"
            : "failed";
      if (mapped !== selected) return false;
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const searchable = `${tx?.id || ""} ${displayType} ${tx?.network || ""}`.toLowerCase();
      if (!searchable.includes(term)) return false;
    }

    return true;
  });

  return filteredRows
    .filter((tx) => Math.abs(Number(tx?.amount || 0)) > 0)
    .map((tx) => {
      const rawAmount = Number(tx.amount || 0);
      const isCredit = isCreditTransaction(tx.type, rawAmount);
      const normalizedStatus = String(tx.status || "completed").toLowerCase();
      const { label: statusLabel, color: statusColor } = mapStatus(normalizedStatus);

      return {
        id: tx.id,
        type: tx.type,
        displayType: formatTransactionTypeLabel(tx.type),
        amount: `${isCredit ? "+" : "-"}${Math.abs(rawAmount).toFixed(2)}${tx.meta?.currency ? ` ${tx.meta.currency}` : ""}`,
        amountRaw: rawAmount,
        status: statusLabel,
        statusColor,
        date: tx.createdAt ? new Date(tx.createdAt).toLocaleDateString() : "",
        time: tx.createdAt ? new Date(tx.createdAt).toLocaleTimeString() : "",
        createdAt: tx.createdAt,
        network: tx.network || "",
      };
    });
};
